// ============================================
// pages/gunny-bags/GunnySupplierPerformance.tsx
// Supplier performance dashboard computed from gunny_suppliers + gunny_pos + gunny_utilization.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Loader2, XCircle, Download } from 'lucide-react';
import { seasonsAPI, gunnySuppliersAPI, gunnyPosAPI, gunnyUtilizationAPI } from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

interface Row {
  supplier_id: number;
  supplier_name: string;
  tender_id?: string;
  pos_count: number;
  po_bags_ordered: number;
  po_total_value: number;
  pos_fulfilled: number;
  pos_pending: number;
  bags_supplied: number;
  bags_utilized: number;
  bags_returned: number;
  on_hand: number;
  return_rate_pct: number;
  utilization_rate_pct: number;
  gross_payable: number;
  payment_released: number;
  payment_pending: number;
  payment_ratio_pct: number;
}

export const GunnySupplierPerformance: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [pos, setPos] = useState<any[]>([]);
  const [utilization, setUtilization] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, sup] = await Promise.all([seasonsAPI.list(), gunnySuppliersAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setSuppliers(sup.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const [p, u] = await Promise.all([
          gunnyPosAPI.list(selectedSeasonId),
          gunnyUtilizationAPI.list(selectedSeasonId),
        ]);
        setPos(Array.isArray(p.data) ? p.data : []);
        setUtilization(Array.isArray(u.data) ? u.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const rows = useMemo((): Row[] => {
    const map: Record<number, Row> = {};
    for (const s of suppliers) {
      map[s.id] = {
        supplier_id: s.id,
        supplier_name: s.supplier_name,
        tender_id: s.tender_id,
        pos_count: 0, po_bags_ordered: 0, po_total_value: 0,
        pos_fulfilled: 0, pos_pending: 0,
        bags_supplied: 0, bags_utilized: 0, bags_returned: 0, on_hand: 0,
        return_rate_pct: 0, utilization_rate_pct: 0,
        gross_payable: 0, payment_released: 0, payment_pending: 0, payment_ratio_pct: 0,
      };
    }
    for (const p of pos) {
      const r = map[p.supplier_id];
      if (!r) continue;
      r.pos_count++;
      r.po_bags_ordered += num(p.total_bags);
      r.po_total_value += num(p.total_amount);
      if (p.status === 'fulfilled' || p.status === 'closed') r.pos_fulfilled++;
      else if (['submitted', 'dm_verified', 'manager_approved'].includes(p.status)) r.pos_pending++;
    }
    for (const u of utilization) {
      const r = map[u.supplier_id];
      if (!r) continue;
      r.bags_supplied += num(u.bags_supplied);
      r.bags_utilized += num(u.bags_utilized);
      r.bags_returned += num(u.bags_returned);
      r.gross_payable += num(u.gross_amount);
      r.payment_released += num(u.payment_released);
    }
    return Object.values(map)
      .map((r) => {
        r.on_hand = Math.max(r.bags_supplied - r.bags_utilized - r.bags_returned, 0);
        r.return_rate_pct = r.bags_supplied > 0 ? (r.bags_returned / r.bags_supplied) * 100 : 0;
        r.utilization_rate_pct = r.bags_supplied > 0 ? (r.bags_utilized / r.bags_supplied) * 100 : 0;
        r.payment_pending = Math.max(r.gross_payable - r.payment_released, 0);
        r.payment_ratio_pct = r.gross_payable > 0 ? (r.payment_released / r.gross_payable) * 100 : 0;
        return r;
      })
      .filter((r) => r.pos_count > 0 || r.bags_supplied > 0)
      .sort((a, b) => b.bags_supplied - a.bags_supplied);
  }, [suppliers, pos, utilization]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(rows, 25);

  const scoreColor = (v: number) => v >= 95 ? 'text-green-700' : v >= 80 ? 'text-yellow-700' : 'text-red-700';

  const handlePrint = () => window.print();

  if (loading && rows.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto print:max-w-none">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <TrendingUp className="w-7 h-7 text-amber-600" />
            Supplier Performance
          </h1>
          <p className="text-sm text-gray-500 mt-1">Rollup across POs, utilization, and payments — per supplier</p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg text-sm">
          <Download className="w-4 h-4" />Print
        </button>
      </div>

      <div className="mb-4 max-w-xs print:hidden">
        <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
        <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
        </select>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" />{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan={2} className="px-3 py-2 text-left font-semibold text-gray-600 uppercase">Supplier</th>
                <th colSpan={3} className="px-3 py-1 text-center font-semibold text-gray-600 uppercase border-l border-gray-200">Purchase Orders</th>
                <th colSpan={4} className="px-3 py-1 text-center font-semibold text-gray-600 uppercase border-l border-gray-200">Bag Flow</th>
                <th colSpan={3} className="px-3 py-1 text-center font-semibold text-gray-600 uppercase border-l border-gray-200">Payment</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase border-l border-gray-200">Total</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Fulfilled</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Pending</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase border-l border-gray-200">Supplied</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Utilized %</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Return %</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">On Hand</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase border-l border-gray-200">Gross</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Settled %</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">No supplier activity in this season.</td></tr>
              ) : pagedItems.map((r) => (
                <tr key={r.supplier_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-gray-900">{r.supplier_name}</div>
                    {r.tender_id && <div className="text-[10px] text-gray-400">Tender {r.tender_id}</div>}
                  </td>
                  <td className="px-2 py-2 text-right border-l border-gray-100">{r.pos_count}</td>
                  <td className="px-2 py-2 text-right text-green-700">{r.pos_fulfilled}</td>
                  <td className="px-2 py-2 text-right text-orange-600">{r.pos_pending}</td>
                  <td className="px-2 py-2 text-right border-l border-gray-100 text-blue-700 font-medium">{r.bags_supplied.toLocaleString('en-IN')}</td>
                  <td className={`px-2 py-2 text-right ${scoreColor(r.utilization_rate_pct)}`}>{r.utilization_rate_pct.toFixed(1)}%</td>
                  <td className={`px-2 py-2 text-right ${r.return_rate_pct < 5 ? 'text-green-700' : r.return_rate_pct < 15 ? 'text-yellow-700' : 'text-red-700'}`}>{r.return_rate_pct.toFixed(1)}%</td>
                  <td className="px-2 py-2 text-right text-purple-700">{r.on_hand.toLocaleString('en-IN')}</td>
                  <td className="px-2 py-2 text-right border-l border-gray-100">{formatAmount(r.gross_payable)}</td>
                  <td className={`px-2 py-2 text-right ${scoreColor(r.payment_ratio_pct)}`}>{r.payment_ratio_pct.toFixed(1)}%</td>
                  <td className="px-2 py-2 text-right text-orange-600">{formatAmount(r.payment_pending)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <div className="px-4 py-3 border-t bg-gray-50 print:hidden">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="suppliers"
            />
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-500 italic">
        Color legend: <span className="text-green-700">green ≥ 95%</span>{' · '}
        <span className="text-yellow-700">yellow 80-94%</span>{' · '}
        <span className="text-red-700">red &lt; 80%</span>
      </div>
    </div>
  );
};

export default GunnySupplierPerformance;
